// DentScribe Voice Scribe � Full App with Google Auth
// ============================================================================
// MODULE IMPORTS
// ============================================================================
import { state } from './state.js';
import { logger, addNetworkLog, getRecentNetworkLogs, redactForLogs, redactKeyShapedValues, sanitizeLogs, addUploadProgressLog, getRecentUploadProgressLogs, addUploadDiagnostic, getRecentUploadDiagnostics, preUploadDiagnostic, createUploadResultTelemetry, initDebugPanel, renderDebugLogs, copyDebugLogs, downloadDebugLogs, apiFetchWithRefresh, getDeviceCapabilities } from './modules/debug.js';
import { openFeedbackModal, closeFeedbackModal, submitFeedback, captureFeedbackScreenshot, removeFeedbackScreenshot, handleFeedbackFileSelect, openTicketList, closeTicketList, showTicketDetail, closeTicketDetail, resolveTicketByUser, refreshTicketDetail, populateFeedbackDiagnostics, hideLiveTranscriptUI, fallbackToAudioUpload } from './modules/feedback.js';
import { loadNotes, loadMoreNotes, filterNotesLocally, renderNotesList, viewNote, copyNote, closeModal, startEditTag, saveEditTag, cancelEditTag, startModalTagEdit, saveModalTag, cancelModalTagEdit, startNoteEdit, saveNoteEdit, cancelNoteEdit, playNoteAudio, toggleReferralPanel, generateReferral, copyReferralLetter, toggleLinkPatientPanel, linkNoteToPatient, unlinkNoteFromPatient, toggleBulkLinkMode, cancelBulkLinkMode, toggleNoteSelect, bulkLinkConfirm } from './modules/notes.js';
import { showAdmin, refreshAdminDashboard, loadAdminUsage, openAdminModal, closeAdminModal, refreshAdminModalData, switchAdminModalTab, triggerBackfill, filterFeedbackTickets, loadAdminPrevPage, loadAdminNextPage, adminFilterByCard, toggleTestAccount, viewTicketScreenshots, viewTicketLogs, updateFeedbackTicket, filterTicketLogs, handleHashNavigation, onAdminOfficeFilterChange, onAdminDateFilterChange, onAdminVisibilityChange } from './modules/admin.js';
import { clearLocalPhiOnSignOut, reapExpiredLocalData, setUserMetaCache, getUserMetaCache, updateAnalyticsMenuVisibility, applyVisibleSections, getCachedVisibleSections, verifySessionToken, handleGoogleSignIn, verifyToken, signOut, showApp, showLoginStatus, showWaitlistForm, submitWaitlist, notifyKeptUnsentAudio, refreshProfilesBackground, _deps as authDeps } from './modules/auth.js';
import { toggleRecording, startRecording, stopRecording, togglePauseResume, pauseRecording, resumeRecordingFromPause, getNote, combineSegments, startAutoSave, stopAutoSave, startTimer, stopTimer, resumeRecording, finishAndUpload, discardRecording, resumeFromHistory, uploadFromHistory, downloadFromHistory, discardFromHistory, loadRecordingHistory, showInterruptedUI, migrateLegacyRecordings, recoverStaleSessions, requestWakeLock, releaseWakeLock, hideRecordingControls, initRecordPatientPicker } from './modules/recording.js';
import { uploadAndProcess, uploadMultipart, uploadSingleXHR, uploadChunkXHR, resolveContentType, pollForCompletion, retryUpload, downloadPendingUpload, discardPendingUpload, handleFileSelect, handleUploadAreaClick, closeFileSelectModal, triggerFileInput, resetForNewRecording, initUploadPatientPicker } from './modules/upload.js';
import { renderProfilePicker, selectProfile, showPinModal, closePinModal, onPinComplete, openProfileMgmt, closeProfileMgmt, showCreateProfileForm, hideProfileForm, submitProfileForm, deleteProfile, switchProfile, startInactivityTracker, stopInactivityTracker, migrateOldNotes, clearProfileSession, submitResetPin, verifyOldPin, hideResetPinForm, updateProfileBadge, setupPinInputHandlers, applyProfileDefaultAppointmentType, hashPin, _deps as profileDeps } from './modules/profiles.js';
import { showAnalytics, hideAnalytics, loadAnalyticsDashboard, onAnalyticsProfileChange, setAnalyticsDateRange, viewAsOffice, exitViewAsMode, loadFeatureFlags, handleFeatureFlagChange, renderProviderProfile, hideProviderProfile, showProviderProfile, showNotesTab, profilePageEditProfile, profilePageResetPin, initNoteFeedback, resetNoteFeedback, selectAppointmentType, initAppointmentTypeSelector, loadAppointmentTypesFromAPI, loadPublicFlags, initNoteModeToggle, isQuickNoteEnabled, updateNoteModeToggleVisibility } from './modules/analytics.js';
import { initFeatureFlags, cleanupFeatureFlags } from './modules/feature-flags.js';
import { openConsentForm, closeConsentModal, clearConsentSignature, submitConsent, downloadConsentPdf, updateConsentBadge, onConsentCheckboxChange, reapExpiredConsents, findConsentForNote } from './modules/consent.js';
import { APP_VERSION, GOOGLE_CLIENT_ID, API_BASE, SESSION_ID, PAGE_LOAD_TIME, sendBeacon, PAGE_TITLES, UNSENT_KEPT_KEY, UNSENT_SESSION_STATUSES, AUTO_LOCK_MINUTES, LOAD_NOTES_DEBOUNCE_MS } from './utils/constants.js';
import { getStoredSessionToken, getStoredSessionTokenOnly, getStoredLegacyToken, getSessionToken, setSessionToken, clearSessionToken, clearUserMetaCache, getAuthHeaders, apiFetch } from './utils/api.js';
import { escapeHtml, escapeJsAttr, showView, navigateTo, toggleSidebar, closeSidebar, showConfirmDialog, showToast, isValidUuid, setStatus } from './utils/dom.js';
import { formatTimeAgo, pad, formatDuration, monotonicNow } from './utils/format.js';
import { RecordingStore, SessionStore, AudioCache } from './utils/storage.js';
import { ProfileStore } from './stores/profile-store.js';
import { AuthStore } from './stores/auth-store.js';
import { NotesStore } from './stores/notes-store.js';

// Wire cross-module deps for auth.js
authDeps.proceedToApp = () => proceedToApp();
authDeps.checkProfilesAndProceed = () => checkProfilesAndProceed();
authDeps.clearProfileSession = () => clearProfileSession();
authDeps.stopInactivityTracker = () => stopInactivityTracker();
authDeps.migrateLegacyRecordings = () => migrateLegacyRecordings();
authDeps.renderProfilePicker = (p) => renderProfilePicker(p);

// Wire cross-module deps for profiles.js
profileDeps.proceedToApp = () => proceedToApp();
profileDeps.checkProfilesAndProceed = () => checkProfilesAndProceed();

window._appLoadTime = PAGE_LOAD_TIME;



// Render Google Sign-In button sized to its container's actual width.
// Must be called when #view-signin is visible so offsetWidth is nonzero.
function renderGoogleButton() {
    if (typeof google === 'undefined' || !google.accounts || !google.accounts.id) return;
    var container = document.getElementById('google-signin-btn');
    if (!container) return;
    var width = container.offsetWidth;
    if (!width) return; // still hidden — caller should retry after view is shown
    // Google SDK caps at 400px max
    width = Math.min(400, Math.floor(width));
    try {
        google.accounts.id.renderButton(container, { theme: 'outline', size: 'large', width: width, text: 'signin_with' });
    } catch (e) { /* SDK not ready */ }
}

// Escape hatch: user taps "Sign in" or "Scan" button on the loading screen.
// Immediately transitions to sign-in view, cancelling any pending init flow.
window.__skipToSignIn = function() {
    sendBeacon('loading_escape_tap');
    document.body.classList.add('js-resolved');
    var loading = document.getElementById('view-loading');
    if (loading) { loading.classList.remove('active'); loading.classList.add('hidden'); }
    var signin = document.getElementById('view-signin');
    if (signin) { signin.classList.remove('hidden'); signin.classList.add('active'); }
    renderGoogleButton();
};


// ============================================================================
// CSP: DELEGATED EVENT DISPATCHER
// Replaces all inline onclick/onchange/oninput/onsubmit handlers so
// 'unsafe-inline' can be removed from CSP script-src.
// Each action name maps to a function call. Dynamic handlers pass data
// via data-* attributes read from el.dataset.
// ============================================================================
const ACTION_MAP = {
    // Auth & Loading
    'skip-to-signin': () => window.__skipToSignIn && window.__skipToSignIn(),
    'sign-out': () => signOut(),
    'start-qr-login': () => startQrLogin(),
    'submit-waitlist': (el, e) => submitWaitlist(e),
    // PIN Modal
    'close-pin-modal': () => closePinModal(),
    // Profile Management Modal
    'close-profile-mgmt': () => closeProfileMgmt(),
    'show-create-profile-form': () => showCreateProfileForm(),
    'submit-profile-form': () => submitProfileForm(),
    'hide-profile-form': () => hideProfileForm(),
    'submit-reset-pin': () => submitResetPin(),
    'verify-old-pin': () => verifyOldPin(),
    'hide-reset-pin-form': () => hideResetPinForm(),
    // Recovery Banner
    'upload-recovered-recording': () => uploadRecoveredRecording(),
    'discard-recovered-recording': () => discardRecoveredRecording(),
    // Recording Controls
    'toggle-recording': () => toggleRecording(),
    'toggle-pause-resume': () => togglePauseResume(),
    'get-note': () => getNote(),
    // Interrupted Actions
    'resume-recording': () => resumeRecording(),
    'finish-and-upload': () => finishAndUpload(),
    'discard-recording': () => discardRecording(),
    // Retry Actions
    'retry-upload': () => retryUpload(),
    'download-pending-upload': () => downloadPendingUpload(),
    'discard-pending-upload': () => discardPendingUpload(),
    // New Recording
    'reset-for-new-recording': () => resetForNewRecording(),
    // Upload Page
    'handle-upload-area-click': () => handleUploadAreaClick(),
    // Notes
    'load-notes': () => loadNotes(true),
    'load-more-notes': () => loadMoreNotes(),
    // Admin
    'refresh-admin-dashboard': () => refreshAdminDashboard(),
    'open-admin-modal': (el) => openAdminModal(el.dataset.modal),
    'trigger-backfill': () => triggerBackfill(),
    'filter-feedback-tickets': (el) => filterFeedbackTickets(el.dataset.filter),
    'close-admin-modal': () => closeAdminModal(),
    'refresh-admin-modal-data': () => refreshAdminModalData(),
    'admin-modal-tab': (el) => switchAdminModalTab(el.dataset.tab),
    // Analytics
    'set-analytics-date-range': (el) => setAnalyticsDateRange(el.dataset.range),
    // File Select Modal
    'close-file-select-modal': () => closeFileSelectModal(),
    'trigger-file-input': () => triggerFileInput(),
    // QR Login Modal
    'cancel-qr-login': () => cancelQrLogin(),
    'regenerate-qr': () => regenerateQr(),
    // Authorize Device Modal
    'confirm-qr-authorize': () => confirmQrAuthorize(),
    'deny-qr-authorize': () => denyQrAuthorize(),
    // QR Scanner Modal
    'stop-qr-scanner': () => stopQrScanner(),
    // Invite QR Modal
    'close-invite-qr': () => closeInviteQr(),
    // Note Modal
    'close-modal': () => closeModal(),
    'copy-note': () => copyNote(),
    // Consent
    'open-consent-form': () => openConsentForm(),
    'submit-consent': () => submitConsent(),
    'clear-consent-signature': () => clearConsentSignature(),
    'close-consent-modal': () => closeConsentModal(),
    'download-consent-pdf': () => {
        const btn = document.getElementById('downloadConsentPdfBtn');
        if (btn) downloadConsentPdf(btn.dataset.patientLabel, btn.dataset.noteDate, btn.dataset.noteId);
    },
    // Feedback Modal
    'close-feedback-modal': () => closeFeedbackModal(),
    'capture-feedback-screenshot': () => captureFeedbackScreenshot(),
    'click-feedback-file-input': () => document.getElementById('feedbackFileInput').click(),
    'submit-feedback': () => submitFeedback(),
    'close-feedback-open-tickets': () => { closeFeedbackModal(); openTicketList(); },
    // Ticket List Modal
    'close-ticket-list': () => closeTicketList(),
    'close-ticket-list-open-feedback': () => { closeTicketList(); openFeedbackModal(); },
    // Ticket Detail Modal
    'close-ticket-detail': () => closeTicketDetail(),
    'close-ticket-detail-open-list': () => { closeTicketDetail(); openTicketList(); },
    // Dynamic: Admin feedback tickets
    'view-ticket-screenshots': (el) => viewTicketScreenshots(el.dataset.id),
    'view-ticket-logs': (el) => viewTicketLogs(el.dataset.id),
    'update-feedback-ticket': (el) => updateFeedbackTicket(el.dataset.id),
    'remove-ticket-logs-modal': () => { const m = document.getElementById('ticketLogsModal'); if (m) m.remove(); },
    'filter-ticket-logs': (el) => filterTicketLogs(el.dataset.filter),
    // Dynamic: Notes
    'view-note': (el) => viewNote(el.dataset.id),
    'start-edit-tag': (el) => startEditTag(el.dataset.id, el.dataset.label),
    'save-edit-tag': (el) => saveEditTag(el.dataset.id),
    'cancel-edit-tag': (el) => cancelEditTag(el.dataset.id, el.dataset.label),
    'play-note-audio': (el) => playNoteAudio(el.dataset.id, el),
    'start-modal-tag-edit': (el) => startModalTagEdit(el.dataset.id),
    'save-modal-tag': (el) => saveModalTag(el.dataset.id),
    'cancel-modal-tag-edit': () => cancelModalTagEdit(),
    'start-note-edit': (el) => startNoteEdit(el.dataset.id),
    'save-note-edit': (el) => saveNoteEdit(el.dataset.id),
    'cancel-note-edit': () => cancelNoteEdit(),
    'toggle-referral-panel': () => toggleReferralPanel(),
    'generate-referral': () => generateReferral(),
    'copy-referral-letter': () => copyReferralLetter(),
    // Link to patient (single note)
    'toggle-link-patient-panel': () => toggleLinkPatientPanel(),
    'link-note-to-patient': () => linkNoteToPatient(),
    'unlink-note-from-patient': () => unlinkNoteFromPatient(),
    // Bulk link many notes to one patient (B3 / FEAT-002)
    'toggle-bulk-link-mode': () => toggleBulkLinkMode(),
    'cancel-bulk-link-mode': () => cancelBulkLinkMode(),
    'toggle-note-select': (el) => toggleNoteSelect(el.dataset.id),
    'bulk-link-confirm': () => bulkLinkConfirm(),
    // Dynamic: check notes / new recording (graceful timeout)
    'click-notes-tab': () => { const nav = document.querySelector('[data-nav="notes"]'); if (nav) nav.click(); },
    // Dynamic: Recording History
    'resume-from-history': (el) => resumeFromHistory(Number(el.dataset.id)),
    'upload-from-history': (el) => uploadFromHistory(Number(el.dataset.id)),
    'download-from-history': (el) => downloadFromHistory(Number(el.dataset.id)),
    'discard-from-history': (el) => discardFromHistory(Number(el.dataset.id)),
    // Dynamic: Admin Pagination
    'load-admin-prev-page': () => loadAdminPrevPage(),
    'load-admin-next-page': () => loadAdminNextPage(),
    'load-admin-usage': () => loadAdminUsage(),
    'admin-filter-card': (el) => adminFilterByCard(el.dataset.name),
    'toggle-test-account': (el) => toggleTestAccount(el.dataset.hash),
    // Dynamic: Feedback screenshot remove
    'remove-feedback-screenshot': (el) => removeFeedbackScreenshot(Number(el.dataset.index)),
    // Dynamic: Ticket list + detail
    'show-ticket-detail': (el) => showTicketDetail(el.dataset.id),
    'resolve-ticket-by-user': (el) => resolveTicketByUser(el.dataset.id),
    'refresh-ticket-detail': (el) => refreshTicketDetail(el.dataset.id),
    // Dynamic: Fallback upload
    'fallback-to-audio-upload': () => fallbackToAudioUpload(),
    // Dynamic: Profile cards
    'select-profile': (el) => selectProfile(el.dataset.id),
    'open-profile-mgmt': () => openProfileMgmt(),
    // Dynamic: Migrate notes
    'migrate-old-notes': () => migrateOldNotes(),
    // Dynamic: Analytics view-as
    'exit-view-as-mode': () => exitViewAsMode(),
    // Dynamic: Provider profile page
    'render-provider-profile': () => renderProviderProfile(),
    'hide-provider-profile': () => hideProviderProfile(),
    'hide-profile-back': () => hideProviderProfile(),
    'hide-profile-show-notes': () => { hideProviderProfile(); showNotesTab(); },
    'profile-page-edit-profile': () => profilePageEditProfile(),
    'profile-page-reset-pin': () => profilePageResetPin(),
};

document.addEventListener('click', function(e) {
    // Stop propagation for elements with data-stop that don't have their own action
    // (e.g. input fields inside clickable cards)
    const stopEl = e.target.closest('[data-stop]');
    const el = e.target.closest('[data-action]');
    if (stopEl && stopEl !== el) {
        // A data-stop element was clicked that isn't itself a data-action — block the parent action
        e.stopPropagation();
        return;
    }
    if (!el) return;
    const action = el.getAttribute('data-action');
    const handler = ACTION_MAP[action];
    if (handler) {
        e.preventDefault();
        if (el.dataset.stop !== undefined) e.stopPropagation();
        handler(el, e);
    }
});

// Unified change/input/submit delegation using CHANGE_ACTION_MAP + ACTION_MAP
const CHANGE_ACTION_MAP = {
    'handle-file-select': (el, e) => handleFileSelect(e),
    'filter-notes-locally': () => filterNotesLocally(),
    'load-notes': () => loadNotes(true),
    'on-admin-date-filter-change': () => onAdminDateFilterChange(),
    'on-admin-office-filter-change': () => onAdminOfficeFilterChange(),
    'on-admin-visibility-change': () => onAdminVisibilityChange(),
    'on-analytics-profile-change': () => onAnalyticsProfileChange(),
    'toggle-resolved-version': (el) => toggleResolvedVersion(el.dataset.id),
    'handle-feedback-file-select': (el, e) => handleFeedbackFileSelect(e),
    'consent-checkbox-change': () => onConsentCheckboxChange(),
    'update-consent-badge': () => updateConsentBadge(),
};

document.addEventListener('change', function(e) {
    const el = e.target.closest('[data-on-change]');
    if (!el) return;
    const action = el.getAttribute('data-on-change');
    const handler = CHANGE_ACTION_MAP[action] || ACTION_MAP[action];
    if (handler) handler(el, e);
});

document.addEventListener('input', function(e) {
    const el = e.target.closest('[data-on-input]');
    if (!el) return;
    const action = el.getAttribute('data-on-input');
    const handler = CHANGE_ACTION_MAP[action] || ACTION_MAP[action];
    if (handler) handler(el, e);
});

document.addEventListener('submit', function(e) {
    const form = e.target.closest('[data-on-submit]');
    if (!form) return;
    const action = form.getAttribute('data-on-submit');
    const handler = ACTION_MAP[action] || CHANGE_ACTION_MAP[action];
    if (handler) {
        e.preventDefault();
        handler(form, e);
    }
});


// ============================================================================
// GLOBAL ERROR AUTO-PUSH — catch unexpected JS errors and push to CloudWatch
// Cap: max 5 error beacons per page load to avoid spam.
// ============================================================================
(function() {
    var errorBeaconCount = 0;
    var MAX_ERROR_BEACONS = 5;
    window.onerror = function(message, source, line, col, error) {
        if (errorBeaconCount >= MAX_ERROR_BEACONS) return;
        errorBeaconCount++;
        sendBeacon('client_error', {
            message: String(message || '').slice(0, 200),
            source: String(source || '').slice(0, 150),
            line: line,
            col: col,
            stack: error && error.stack ? String(error.stack).slice(0, 500) : null
        });
    };
    window.addEventListener('unhandledrejection', function(event) {
        if (errorBeaconCount >= MAX_ERROR_BEACONS) return;
        errorBeaconCount++;
        var reason = event.reason;
        var message = reason instanceof Error ? reason.message : String(reason || '');
        var stack = reason instanceof Error ? reason.stack : null;
        sendBeacon('client_error', {
            message: String(message).slice(0, 200),
            source: 'unhandledrejection',
            line: null,
            col: null,
            stack: stack ? String(stack).slice(0, 500) : null
        });
    });
})();

// ============================================================================

// ============================================================================
// STORE WINDOW BRIDGES — keeps existing reads/writes working via window globals
// ============================================================================

// ProfileStore getter/setter bridges
Object.defineProperty(window, 'currentProfileId', {
    get: function() { return ProfileStore.get('currentProfileId'); },
    set: function(v) { ProfileStore.set('currentProfileId', v); ProfileStore.persistSession(); },
    configurable: true,
});
Object.defineProperty(window, 'currentProfileDisplayName', {
    get: function() { return ProfileStore.get('currentDisplayName'); },
    set: function(v) { ProfileStore.set('currentDisplayName', v); ProfileStore.persistSession(); },
    configurable: true,
});
Object.defineProperty(window, 'currentProfileColor', {
    get: function() { return ProfileStore.get('currentColor'); },
    set: function(v) { ProfileStore.set('currentColor', v); ProfileStore.persistSession(); },
    configurable: true,
});
Object.defineProperty(window, 'isProfileAdmin', {
    get: function() { return ProfileStore.get('state.isAdmin'); },
    set: function(v) { ProfileStore.set('state.isAdmin', v); ProfileStore.persistSession(); },
    configurable: true,
});
Object.defineProperty(window, 'profilesData', {
    get: function() {
        var profiles = ProfileStore.get('profiles');
        return { profiles: profiles || [], profilesEnabled: ProfileStore.get('profilesEnabled') };
    },
    set: function(v) {
        if (v && v.profiles) {
            ProfileStore.setAll({ profiles: v.profiles, profilesEnabled: !!v.profilesEnabled });
        } else {
            ProfileStore.setAll({ profiles: [], profilesEnabled: false });
        }
    },
    configurable: true,
});

// ProfileStore subscribers — auto-update UI when profile state changes
ProfileStore.on('currentDisplayName', function() { updateProfileBadge(); });
ProfileStore.on('currentColor', function() { updateProfileBadge(); });
ProfileStore.on('currentProfileId', function() { updateProfileBadge(); });
ProfileStore.on('profiles', function() {
    var modal = document.getElementById('profileMgmtModal');
    if (modal && modal.classList.contains('active')) {
        loadProfileMgmtList();
    }
});

// AuthStore backward compat bridges
Object.defineProperty(window, 'authStoreCurrentUser', {
    get: function() { return AuthStore.get('state.currentUser'); },
    set: function(v) { AuthStore.set('state.currentUser', v); },
    configurable: true,
});
Object.defineProperty(window, 'authStoreIsAdmin', {
    get: function() { return AuthStore.get('state.isAdmin'); },
    set: function(v) { AuthStore.set('state.isAdmin', v); },
    configurable: true,
});
Object.defineProperty(window, 'authStoreIdToken', {
    get: function() { return AuthStore.get('state.idToken'); },
    set: function(v) { AuthStore.set('state.idToken', v); },
    configurable: true,
});

// ============================================================================
// GOOGLE SIGN-IN — window.onload handles everything (SDK init + auth check)
// ============================================================================
// Device-capability census beacon — single-fire per session, fire-and-forget.
// Posts ONLY the 7 non-PHI capability fields to POST /telemetry/device (auth
// optional). Any failure is swallowed so it can never block or break startup.
let __deviceCapabilitySent = false;
function sendDeviceCapabilityBeacon() {
    if (__deviceCapabilitySent) return;
    __deviceCapabilitySent = true;
    Promise.resolve()
        .then(getDeviceCapabilities)
        .then(function(caps) {
            logger.info('TELEMETRY', 'Device capability census', caps);
            const headers = { 'Content-Type': 'application/json' };
            const token = getStoredSessionTokenOnly && getStoredSessionTokenOnly();
            if (token) headers['Authorization'] = 'Bearer ' + token;
            return fetch(API_BASE + '/telemetry/device', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(caps),
                keepalive: true,
            });
        })
        .catch(function() { /* telemetry is best-effort; never surface to the user */ });
}

window.onload = function() {
    // Pre-auth beacon: proves JS executed and window.onload fired
    sendBeacon('page_load');

    // Device-capability census (non-PHI, once per session, fire-and-forget).
    // Never awaited, never throws into startup — a telemetry failure must not
    // break or delay the app. Builds the WebGPU/on-device-Whisper feasibility data.
    sendDeviceCapabilityBeacon();

    // Check for QR login parameter — must happen before any auth logic
    const urlParams = new URLSearchParams(window.location.search);
    const qrParam = urlParams.get('qr');
    if (qrParam) {
        window.history.replaceState({}, '', window.location.pathname);
        state.pendingQrSession = qrParam;
    }

    // Check for invite parameter — device authorization via QR invite link
    const inviteParam = urlParams.get('invite');
    if (inviteParam) {
        window.history.replaceState({}, '', window.location.pathname);
        // Only redeem if user is NOT already authenticated
        const existingToken = getStoredSessionTokenOnly();
        if (!existingToken) {
            redeemInviteCode(inviteParam);
            return; // Skip normal auth flow — invite redemption handles it
        }
        // Already authenticated — ignore invite, proceed normally
    }

    // Safety-net timeout (belt): if init hasn't moved past the loading spinner within 8s,
    // fall through to sign-in. Covers iOS Chrome where FedCM/One Tap prompt never
    // fires its callback, leaving the user stuck on the spinner forever.
    // Note: the PRIMARY safety-net is the inline <script> in index.html (suspenders)
    // which fires even if window.onload never triggers due to a CDN script stall.
    var initResolved = false;
    var initTimeout = setTimeout(function() {
        if (!initResolved && !window.__dentscribeInitSafetyFired) {
            var loadingEl = document.getElementById('view-loading');
            if (loadingEl && loadingEl.classList.contains('active')) {
                logger.warn('AUTH', 'Init timeout — loading spinner still visible after 8s, showing sign-in');
                showView('view-signin');
                requestAnimationFrame(renderGoogleButton);
            }
        }
    }, 8000);

    function markInitResolved() {
        initResolved = true;
        clearTimeout(initTimeout);
        // Also cancel the inline safety-net timer from index.html
        if (window.__dentscribeInitSafetyTimer) {
            clearTimeout(window.__dentscribeInitSafetyTimer);
            window.__dentscribeInitSafetyTimer = null;
        }
    }

    // Initialize Google Identity Services SDK
    if (typeof google === 'undefined' || !google.accounts) {
        logger.warn('AUTH', 'Google SDK not available at window.onload');
        markInitResolved();
        showView('view-signin');
        requestAnimationFrame(renderGoogleButton);
        return;
    }
    google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleSignIn,
        auto_select: true,
        use_fedcm_for_prompt: true,
    });
    // Initial render attempt — may be zero-width if view is hidden.
    // Re-render happens via showView('view-signin') path below.
    renderGoogleButton();

    // Check for stored session token
    const sessionToken = getStoredSessionTokenOnly();
    if (sessionToken) {
        state.idToken = sessionToken;
        markInitResolved();
        verifySessionToken(sessionToken);
        return;
    }

    // Migration: check for legacy Google token
    const legacyToken = getStoredLegacyToken();
    if (legacyToken) {
        state.idToken = legacyToken;
        markInitResolved();
        verifyToken(legacyToken);
        return;
    }

    // No stored token — trigger One Tap for auto-select.
    // On iOS Chrome (WKWebView), FedCM may not fire the callback at all,
    // so the 8s timeout above ensures we never hang indefinitely.
    google.accounts.id.prompt((notification) => {
        markInitResolved();
        if (notification.isSkippedMoment() || notification.isDismissedMoment()) {
            showView('view-signin');
            requestAnimationFrame(renderGoogleButton);
        }
    });
};


let _sidebarNavInitialized = false;
function initSidebarNav() {
    if (_sidebarNavInitialized) return;
    _sidebarNavInitialized = true;
    const sidebar = document.getElementById('appSidebar');
    if (!sidebar) return;
    sidebar.addEventListener('click', function(e) {
        const navItem = e.target.closest('.pa-nav-item[data-nav]');
        if (!navItem) return;
        const nav = navItem.dataset.nav;
        // Handle action items (not page navigation)
        switch (nav) {
            case 'switch-profile':
                switchProfile();
                return;
            case 'scan-qr':
                openQrScanner();
                closeSidebar();
                return;
            case 'authorize':
                startInviteQr();
                closeSidebar();
                return;
            case 'manage-profiles':
                openProfileMgmt();
                closeSidebar();
                return;
            case 'feedback':
                openFeedbackModal();
                closeSidebar();
                return;
            case 'sign-out':
                signOut();
                return;
        }
        // Handle page navigation
        switch (nav) {
            case 'record':
            case 'upload':
            case 'notes':
                navigateTo(nav);
                if (nav === 'notes') loadNotes();
                // Clear hash for main pages
                if (location.hash) history.replaceState(null, '', location.pathname + location.search);
                break;
            case 'admin':
                showAdmin();
                break;
            case 'analytics':
                showAnalytics();
                break;
            case 'profile':
                showProviderProfile();
                break;
            case 'feature-flags':
                showFeatureFlags();
                break;
        }
    });
    // Sidebar toggle and overlay
    const toggle = document.getElementById('appSidebarToggle');
    if (toggle) toggle.addEventListener('click', toggleSidebar);
    const overlay = document.getElementById('appSidebarOverlay');
    if (overlay) overlay.addEventListener('click', closeSidebar);
}

// ============================================================================
// FEATURE FLAGS PAGE
// ============================================================================

let _ffRetryWired = false;
function showFeatureFlags() {
    cleanupFeatureFlags(); // Clear stale state from previous visit
    navigateTo('feature-flags');
    location.hash = '#feature-flags';
    initFeatureFlags();
    // Wire retry button once
    if (!_ffRetryWired) {
        _ffRetryWired = true;
        const retryBtn = document.getElementById('ffRetryBtn');
        if (retryBtn) retryBtn.addEventListener('click', () => initFeatureFlags());
    }
}

function hideFeatureFlags() {
    if (location.hash === '#feature-flags') {
        history.replaceState(null, '', location.pathname + location.search);
    }
    cleanupFeatureFlags();
}


async function checkProfilesAndProceed() {
    try {
        const res = await apiFetch('/profiles');
        if (res.ok) {
            const data = await res.json();
            profilesData = data;

            if (data.profilesEnabled && data.profiles && data.profiles.length > 0) {
                // Profiles exist — check if we already have a valid profile session
                if (currentProfileId) {
                    // Verify the stored profile still exists
                    const profileExists = data.profiles.some(p => p.profileId === currentProfileId);
                    if (profileExists) {
                        proceedToApp();
                        return;
                    }
                    // Profile was deleted — clear and show picker
                    clearProfileSession();
                }
                // Show profile picker
                renderProfilePicker(data.profiles);
                showView('view-profiles');
                return;
            }

            // No profiles exist yet — show "Create Your Profile" form
            showView('view-create-first-profile');
            initFirstProfileForm();
            return;
        }
    } catch (err) {
        logger.warn('PROFILES', 'Failed to fetch profiles, proceeding without', { error: err.message });
    }
    // Fallback — proceed directly to app (should rarely happen)
    proceedToApp();
}

// ============================================================================
// FIRST PROFILE CREATION — shown when no profiles exist (replaces auto-bootstrap)
// ============================================================================

function initFirstProfileForm() {
    const form = document.getElementById('firstProfileForm');
    const palette = document.getElementById('fpColorPalette');

    // Color swatch selection
    palette.addEventListener('click', function(e) {
        const swatch = e.target.closest('.fp-color-swatch');
        if (!swatch) return;
        palette.querySelectorAll('.fp-color-swatch').forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
    });

    // PIN auto-advance for both PIN groups
    setupPinAutoAdvance('fpPin');
    setupPinAutoAdvance('fpPinConfirm');

    // Form submission
    form.addEventListener('submit', function(e) {
        e.preventDefault();
        submitFirstProfile();
    });
}

function setupPinAutoAdvance(prefix) {
    for (let i = 1; i <= 4; i++) {
        const el = document.getElementById(prefix + i);
        el.addEventListener('input', function() {
            this.value = this.value.replace(/[^0-9]/g, '');
            if (this.value.length === 1 && i < 4) {
                document.getElementById(prefix + (i + 1)).focus();
            }
        });
        el.addEventListener('keydown', function(e) {
            if (e.key === 'Backspace' && !this.value && i > 1) {
                document.getElementById(prefix + (i - 1)).focus();
            }
        });
    }
}

function getFirstProfilePin(prefix) {
    let pin = '';
    for (let i = 1; i <= 4; i++) {
        pin += (document.getElementById(prefix + i).value || '');
    }
    return pin;
}

function showFirstProfileError(msg) {
    const el = document.getElementById('fpError');
    el.textContent = msg;
    el.classList.remove('d-none');
}

function hideFirstProfileError() {
    document.getElementById('fpError').classList.add('d-none');
}

async function submitFirstProfile() {
    hideFirstProfileError();

    const displayName = document.getElementById('fpDisplayName').value.trim();
    const role = document.getElementById('fpRole').value;
    const activeSwatch = document.querySelector('#fpColorPalette .fp-color-swatch.active');
    const color = activeSwatch ? activeSwatch.dataset.color : '#4A90D9';
    const pin = getFirstProfilePin('fpPin');
    const pinConfirm = getFirstProfilePin('fpPinConfirm');

    // Validation
    if (!displayName) { showFirstProfileError('Please enter a display name.'); return; }
    if (displayName.length > 30) { showFirstProfileError('Display name must be 30 characters or less.'); return; }
    if (!role) { showFirstProfileError('Please select a role.'); return; }
    if (pin.length !== 4) { showFirstProfileError('Please enter a 4-digit PIN.'); return; }
    if (pinConfirm.length !== 4) { showFirstProfileError('Please confirm your PIN.'); return; }
    if (pin !== pinConfirm) { showFirstProfileError('PINs do not match. Please try again.'); return; }

    // Show loading
    const btn = document.getElementById('fpSubmitBtn');
    const btnText = document.getElementById('fpSubmitText');
    const spinner = document.getElementById('fpSubmitSpinner');
    btn.disabled = true;
    btnText.textContent = 'Creating...';
    spinner.classList.remove('d-none');

    try {
        const pinHash = await hashPin(pin);
        const res = await apiFetchWithRefresh('/profiles', {
            method: 'POST',
            body: JSON.stringify({ displayName, role, color, pinHash }),
        });

        if (res.ok) {
            const data = await res.json();
            const profile = data.profile;
            logger.info('PROFILES', 'First profile created', {
                profileId: profile.profileId,
                notesMigrated: data.notesMigrated,
            });

            // Store profile session (same pattern as onPinComplete)
            currentProfileId = profile.profileId;
            currentProfileDisplayName = profile.displayName || displayName;
            currentProfileColor = profile.color || color;
            isProfileAdmin = true; // First profile is always admin
            sessionStorage.setItem('dentscribe_profileId', currentProfileId);
            sessionStorage.setItem('dentscribe_profileDisplayName', currentProfileDisplayName);
            sessionStorage.setItem('dentscribe_profileColor', currentProfileColor);
            sessionStorage.setItem('dentscribe_isProfileAdmin', '1');

            proceedToApp();
            return;
        }

        if (res.status === 409) {
            // Already bootstrapped by another tab/device — re-fetch and show picker
            logger.info('PROFILES', 'Profile already exists (409), showing picker');
            const refetchRes = await apiFetch('/profiles');
            if (refetchRes.ok) {
                const refetchData = await refetchRes.json();
                profilesData = refetchData;
                if (refetchData.profiles && refetchData.profiles.length > 0) {
                    renderProfilePicker(refetchData.profiles);
                    showView('view-profiles');
                    return;
                }
            }
        }

        // Other errors
        const errData = await res.json().catch(() => ({}));
        showFirstProfileError(errData.error || 'Failed to create profile. Please try again.');
    } catch (err) {
        logger.error('PROFILES', 'First profile creation failed', err);
        showFirstProfileError('Network error. Please check your connection and try again.');
    } finally {
        btn.disabled = false;
        btnText.textContent = 'Create Profile';
        spinner.classList.add('d-none');
    }
}

function proceedToApp() {
    showView('view-app');
    document.getElementById('sidebarVersion').textContent = 'v' + APP_VERSION;
    // Single call to apply all section visibility from cached auth response
    applyVisibleSections(getCachedVisibleSections());
    // Show profile badge if a profile is active
    updateProfileBadge();
    initSidebarNav();
    // Migrate legacy IndexedDB entries to SessionStore
    migrateLegacyRecordings();
    // Mark stale 'recording' sessions as 'interrupted' (crash recovery)
    recoverStaleSessions();
    // Load recording history
    loadRecordingHistory();
    // Clean old completed sessions
    SessionStore.cleanOld();
    // FIX C1: age-based reaper for on-device PHI past the retention window
    reapExpiredLocalData();
    // Reap expired consent forms (same retention window)
    reapExpiredConsents();
    // FIX C1: tell the user about unsent audio kept across a sign-out
    notifyKeptUnsentAudio();
    // Start inactivity tracker if profile is active
    if (currentProfileId) {
        startInactivityTracker();
    }
    // Reset recording UI state (handles page refresh during active recording)
    // FIX A1: this function nulls the timer base below, so it MUST stop the timer
    // first. Previously a surviving interval kept calling updateTimer() with a
    // null base and rendered a nonsense duration.
    stopTimer();
    state.startTimeMono = null;
    document.getElementById('timer').textContent = '00:00:00';
    document.getElementById('timer').classList.remove('paused');
    hideRecordingControls();
    document.getElementById('recordBtn').style.display = '';
    state.isRecording = false;
    state.isPaused = false;
    state.elapsedBeforePause = 0;
    state.startTime = null;
    state.currentSessionDbId = null;
    state.currentSessionId = null;

    // If opened via QR code scan on phone, show authorize dialog
    if (state.pendingQrSession) {
        showAuthorizeDeviceModal();
    }

    // Bug #5 fix: restore analytics view from URL hash after page refresh
    if (location.hash === '#analytics') {
        setTimeout(function() { showAnalytics(); }, 100);
    } else if (location.hash === '#profile') {
        setTimeout(function() { showProviderProfile(); }, 100);
    } else if (location.hash === '#admin' && state.isAdmin) {
        setTimeout(function() { showAdmin(); }, 100);
    } else if (state.isAdmin) {
        // Only SUPER ADMIN auto-redirects to admin panel
        setTimeout(function() { showAdmin(); }, 100);
    }
    // isProfileAdmin (office admin) stays on recording view — their primary task is charting

    // BUG-2 fix: listen for hash changes while app is running
    window.addEventListener('hashchange', handleHashNavigation);

    // Load appointment types from API (auth is confirmed at this point)
    loadAppointmentTypesFromAPI();
    // Load public feature flags (Quick Note toggle visibility)
    loadPublicFlags();
}


function showInputError(inputEl, errorElId) {
    inputEl.classList.add('input-error');
    const errorEl = document.getElementById(errorElId);
    if (errorEl) errorEl.classList.remove('hidden');
    // Focus the input
    inputEl.focus();
    // Remove error on typing
    const handler = () => {
        inputEl.classList.remove('input-error');
        if (errorEl) errorEl.classList.add('hidden');
        inputEl.removeEventListener('input', handler);
    };
    inputEl.addEventListener('input', handler);
}

// ============================================================================
// HELPERS
// ============================================================================
function showProgress(pEl, fEl, pct) {
    document.getElementById(pEl).classList.add('active');
    document.getElementById(fEl).style.width = pct + '%';
}
function hideProgress(pEl) { document.getElementById(pEl).classList.remove('active'); }

// Modal close on backdrop click
document.getElementById('noteModal').addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeModal();
});

// File select modal close on backdrop click
document.getElementById('fileSelectModal').addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeFileSelectModal();
});


// QR CODE LOGIN (PC side — shows QR, polls for authorization)
// ============================================================================
async function startQrLogin() {
    clearInterval(state.qrPollInterval);
    clearInterval(state.qrTimerInterval);
    state.qrPollInterval = null;
    state.qrTimerInterval = null;
    try {
        const res = await fetch(API_BASE + '/auth/qr-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to create QR session');
        const data = await res.json();

        state.qrSessionId = data.sessionId;
        const qrUrl = data.qrUrl;

        // Show QR modal
        document.getElementById('qrLoginModal').classList.add('active');
        document.getElementById('qrLoginStatus').innerHTML = '<span class="qr-pulse-dot"></span> Waiting for scan...';
        document.getElementById('qrLoginStatus').className = 'qr-status';
        document.getElementById('btnRegenerateQr').classList.add('hidden');

        // Generate QR code on canvas using QRious
        new QRious({
            element: document.getElementById('qrCanvas'),
            value: qrUrl,
            size: 256,
            foreground: '#1a73e8',
            background: '#ffffff',
            level: 'M',
        });

        // Start countdown timer
        startQrTimer(300);

        // Start polling
        state.qrPollInterval = setInterval(() => pollQrStatus(state.qrSessionId), 2000);

    } catch (err) {
        logger.error('AUTH', 'QR login session creation failed', err);
        showToast('Failed to start QR login. Please try again.', 'error');
    }
}

function cancelQrLogin() {
    clearInterval(state.qrPollInterval);
    clearInterval(state.qrTimerInterval);
    state.qrPollInterval = null;
    state.qrTimerInterval = null;
    state.qrSessionId = null;
    document.getElementById('qrLoginModal').classList.remove('active');
}

function regenerateQr() {
    cancelQrLogin();
    startQrLogin();
}

// ============================================================================
// IN-APP QR SCANNER (for phone scanning PC's QR code)
// ============================================================================

let html5QrScanner = null;

function openQrScanner() {
    document.getElementById('qrScannerModal').classList.add('active');
    document.getElementById('qrScanStatus').textContent = '';
    document.getElementById('qrScanStatus').className = 'qr-scan-status';

    html5QrScanner = new Html5Qrcode("qrScannerView");
    html5QrScanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        onQrCodeScanned,
        (errorMessage) => {}
    ).catch(err => {
        document.getElementById('qrScanStatus').textContent = 'Camera access denied. Please allow camera permission.';
        document.getElementById('qrScanStatus').className = 'qr-scan-status error';
    });
}

function stopQrScanner() {
    if (html5QrScanner) {
        try {
            html5QrScanner.stop().then(() => {
                try { html5QrScanner.clear(); } catch(e) {}
                html5QrScanner = null;
            }).catch(() => { html5QrScanner = null; });
        } catch(e) { html5QrScanner = null; }
    }
    document.getElementById('qrScannerModal').classList.remove('active');
}

async function onQrCodeScanned(decodedText) {
    if (!decodedText.includes('scribe.dentpro.ai')) {
        document.getElementById('qrScanStatus').textContent = 'Not a valid DentScribe QR code.';
        document.getElementById('qrScanStatus').className = 'qr-scan-status error';
        return;
    }

    const url = new URL(decodedText);

    // Handle invite QR codes (?invite= parameter)
    const inviteCode = url.searchParams.get('invite');
    if (inviteCode) {
        // Stop scanner immediately to prevent duplicate scans
        if (html5QrScanner) { html5QrScanner.stop().catch(() => {}); }

        document.getElementById('qrScanStatus').textContent = 'Authorizing device...';
        document.getElementById('qrScanStatus').className = 'qr-scan-status';

        try {
            const res = await fetch(API_BASE + '/auth/qr-redeem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ inviteCode: inviteCode }),
            });
            const data = await res.json();

            if (res.ok && data.status === 'approved' && data.sessionToken) {
                setSessionToken(data.sessionToken);
                state.currentUser = data.user;
                state.isAdmin = data.isAdmin || false;
                state.idToken = data.sessionToken;
                if (data.visibleSections) applyVisibleSections(data.visibleSections);
                setUserMetaCache({ isAdmin: state.isAdmin, visibleSections: data.visibleSections, subscriptionStatus: data.subscriptionStatus, trialEndDate: data.trialEndDate });
                logger.info('AUTH', 'Invite redeemed via QR scan', { isAdmin: state.isAdmin });

                document.getElementById('qrScanStatus').textContent = '\u2713 Authorized! Logging in...';
                document.getElementById('qrScanStatus').className = 'qr-scan-status success';
                setTimeout(() => {
                    stopQrScanner();
                    showApp();
                }, 1500);
            } else {
                document.getElementById('qrScanStatus').textContent = 'Error: ' + (data.error || 'Invite invalid or expired');
                document.getElementById('qrScanStatus').className = 'qr-scan-status error';
            }
        } catch (err) {
            document.getElementById('qrScanStatus').textContent = 'Network error. Try again.';
            document.getElementById('qrScanStatus').className = 'qr-scan-status error';
        }
        return;
    }

    // Handle existing QR login codes (?qr= parameter)
    const sessionId = url.searchParams.get('qr');
    if (!sessionId) {
        document.getElementById('qrScanStatus').textContent = 'Not a valid DentScribe QR code.';
        document.getElementById('qrScanStatus').className = 'qr-scan-status error';
        return;
    }

    // Stop scanner immediately to prevent duplicate scans
    if (html5QrScanner) { html5QrScanner.stop().catch(() => {}); }

    document.getElementById('qrScanStatus').textContent = 'Authorizing device...';
    document.getElementById('qrScanStatus').className = 'qr-scan-status';

    try {
        const res = await apiFetch('/auth/qr-authorize', {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
        });
        const data = await res.json();

        if (res.ok && data.status === 'authorized') {
            document.getElementById('qrScanStatus').textContent = '\u2713 Device authorized!';
            document.getElementById('qrScanStatus').className = 'qr-scan-status success';
            setTimeout(() => stopQrScanner(), 2000);
        } else {
            document.getElementById('qrScanStatus').textContent = 'Error: ' + (data.error || 'Failed');
            document.getElementById('qrScanStatus').className = 'qr-scan-status error';
        }
    } catch (err) {
        document.getElementById('qrScanStatus').textContent = 'Network error. Try again.';
        document.getElementById('qrScanStatus').className = 'qr-scan-status error';
    }
}

async function pollQrStatus(sessionId) {
    try {
        const res = await fetch(API_BASE + '/auth/qr-status/' + sessionId, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();

        if (data.status === 'authorized') {
            clearInterval(state.qrPollInterval);
            clearInterval(state.qrTimerInterval);
            state.qrPollInterval = null;
            state.qrTimerInterval = null;
            handleQrAuthorized(data);
        } else if (data.status === 'expired') {
            clearInterval(state.qrPollInterval);
            clearInterval(state.qrTimerInterval);
            state.qrPollInterval = null;
            state.qrTimerInterval = null;
            document.getElementById('qrLoginStatus').innerHTML = '⏰ QR code expired';
            document.getElementById('qrLoginStatus').className = 'qr-status expired';
            document.getElementById('qrTimer').textContent = 'Expired';
            document.getElementById('btnRegenerateQr').classList.remove('hidden');
        }
    } catch (err) {
        logger.warn('AUTH', 'QR poll error', { error: err.message });
    }
}

async function handleQrAuthorized(data) {
    document.getElementById('qrLoginStatus').innerHTML = '✓ Authorized! Logging in...';
    document.getElementById('qrLoginStatus').className = 'qr-status success';

    // Backend now returns authCode instead of token — exchange it via /auth/session
    const authCode = data.authCode || data.token;
    if (!authCode) {
        document.getElementById('qrLoginStatus').innerHTML = '✕ Authorization failed — no auth code received';
        document.getElementById('qrLoginStatus').className = 'qr-status expired';
        return;
    }

    try {
        const res = await fetch(API_BASE + '/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ authCode: authCode }),
        });
        const sessionData = await res.json();

        if (sessionData.status === 'approved' && sessionData.sessionToken) {
            setSessionToken(sessionData.sessionToken); // FIX B12: single accessor
            state.currentUser = sessionData.user;
            state.isAdmin = sessionData.isAdmin || false;
            state.idToken = sessionData.sessionToken;
            logger.info('AUTH', 'QR login session exchanged successfully', { isAdmin: state.isAdmin });

            // Brief delay for visual feedback
            setTimeout(() => {
                document.getElementById('qrLoginModal').classList.remove('active');
                showApp();
            }, 1000);
        } else {
            logger.warn('AUTH', 'QR auth code exchange failed', { status: sessionData.status });
            document.getElementById('qrLoginStatus').innerHTML = '✕ Login failed — session exchange rejected';
            document.getElementById('qrLoginStatus').className = 'qr-status expired';
        }
    } catch (err) {
        logger.error('AUTH', 'QR auth code exchange network error', err, { online: navigator.onLine });
        document.getElementById('qrLoginStatus').innerHTML = '✕ Network error during login';
        document.getElementById('qrLoginStatus').className = 'qr-status expired';
    }
}

function startQrTimer(seconds) {
    let remaining = seconds;
    const timerEl = document.getElementById('qrTimer');
    timerEl.textContent = 'Expires in ' + formatQrTime(remaining);

    state.qrTimerInterval = setInterval(() => {
        remaining--;
        if (remaining <= 0) {
            clearInterval(state.qrTimerInterval);
            state.qrTimerInterval = null;
            timerEl.textContent = 'Expired';
            return;
        }
        timerEl.textContent = 'Expires in ' + formatQrTime(remaining);
    }, 1000);
}

function formatQrTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m + ':' + s.toString().padStart(2, '0');
}

// ============================================================================
// QR CODE LOGIN (Phone side — authorize device)
// ============================================================================
function showAuthorizeDeviceModal() {
    document.getElementById('authorizeDeviceModal').classList.add('active');
    document.getElementById('authorizeStatus').textContent = '';
}

async function confirmQrAuthorize() {
    const statusEl = document.getElementById('authorizeStatus');
    statusEl.textContent = 'Authorizing...';
    statusEl.style.color = 'var(--primary)';

    try {
        const res = await apiFetch('/auth/qr-authorize', {
            method: 'POST',
            body: JSON.stringify({ sessionId: state.pendingQrSession }),
        });
        const data = await res.json();

        if (res.ok && data.status === 'authorized') {
            statusEl.textContent = '✓ Device authorized!';
            statusEl.style.color = 'var(--success)';
            state.pendingQrSession = null;
            setTimeout(() => {
                document.getElementById('authorizeDeviceModal').classList.remove('active');
            }, 1500);
        } else {
            statusEl.textContent = '✕ ' + (data.error || 'Authorization failed');
            statusEl.style.color = 'var(--danger)';
        }
    } catch (err) {
        statusEl.textContent = '✕ Network error. Try again.';
        statusEl.style.color = 'var(--danger)';
    }
}

function denyQrAuthorize() {
    state.pendingQrSession = null;
    document.getElementById('authorizeDeviceModal').classList.remove('active');
}

// ============================================================================
// QR INVITE — "Authorize Device" (PC generates invite QR for another device)
// ============================================================================

let inviteTimerInterval = null;

async function startInviteQr() {
    clearInterval(inviteTimerInterval);
    inviteTimerInterval = null;

    const modal = document.getElementById('inviteQrModal');
    const statusEl = document.getElementById('inviteQrStatus');
    const timerEl = document.getElementById('inviteQrTimer');
    const canvas = document.getElementById('inviteQrCanvas');

    modal.classList.add('active');
    statusEl.textContent = 'Generating invite...';
    statusEl.className = 'qr-status';
    timerEl.textContent = '';

    try {
        const res = await apiFetchWithRefresh('/auth/qr-invite', {
            method: 'POST',
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to create invite');
        }
        const data = await res.json();

        // Render QR code
        new QRious({
            element: canvas,
            value: data.qrUrl,
            size: 256,
            foreground: '#1a73e8',
            background: '#ffffff',
            level: 'M',
        });

        statusEl.innerHTML = '<span class="qr-pulse-dot"></span> Scan this QR from another device to log it in';
        statusEl.className = 'qr-status';

        // Start countdown
        let remaining = data.expiresIn || 300;
        timerEl.textContent = 'Expires in ' + formatQrTime(remaining);
        inviteTimerInterval = setInterval(() => {
            remaining--;
            if (remaining <= 0) {
                clearInterval(inviteTimerInterval);
                inviteTimerInterval = null;
                timerEl.textContent = 'Expired';
                statusEl.innerHTML = '\u23F0 Invite expired';
                statusEl.className = 'qr-status expired';
                return;
            }
            timerEl.textContent = 'Expires in ' + formatQrTime(remaining);
        }, 1000);

    } catch (err) {
        logger.error('AUTH', 'Invite QR generation failed', err);
        statusEl.textContent = 'Failed to generate invite. Try again.';
        statusEl.className = 'qr-status expired';
    }
}

function closeInviteQr() {
    clearInterval(inviteTimerInterval);
    inviteTimerInterval = null;
    document.getElementById('inviteQrModal').classList.remove('active');
}

// ============================================================================
// INVITE REDEMPTION — unauthenticated device redeems invite code
// ============================================================================

async function redeemInviteCode(inviteCode) {
    // Show loading state
    showView('view-loading');
    const loadingEl = document.querySelector('#view-loading .text-center');
    if (loadingEl) loadingEl.innerHTML = '<div class="spinner-border text-primary mb-3" role="status"></div><p class="text-muted">Authorizing device...</p>';

    try {
        const res = await fetch(API_BASE + '/auth/qr-redeem', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ inviteCode: inviteCode }),
        });
        const data = await res.json();

        if (res.ok && data.status === 'approved' && data.sessionToken) {
            setSessionToken(data.sessionToken);
            state.currentUser = data.user;
            state.isAdmin = data.isAdmin || false;
            state.idToken = data.sessionToken;
            if (data.visibleSections) applyVisibleSections(data.visibleSections);
            setUserMetaCache({ isAdmin: state.isAdmin, visibleSections: data.visibleSections, subscriptionStatus: data.subscriptionStatus, trialEndDate: data.trialEndDate });
            logger.info('AUTH', 'Invite redeemed successfully', { isAdmin: state.isAdmin });
            showApp();
        } else {
            const errorMsg = data.error || 'Invite code is invalid or expired.';
            logger.warn('AUTH', 'Invite redemption failed', { status: res.status });
            showView('view-signin');
            requestAnimationFrame(renderGoogleButton);
            showLoginStatus(errorMsg);
        }
    } catch (err) {
        logger.error('AUTH', 'Invite redemption network error', err);
        showView('view-signin');
        requestAnimationFrame(renderGoogleButton);
        showLoginStatus('Network error during device authorization. Please try again.');
    }
}

// Modal close on backdrop click for QR modals
document.addEventListener('DOMContentLoaded', () => {
    const qrModal = document.getElementById('qrLoginModal');
    if (qrModal) qrModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) cancelQrLogin(); });
    const authModal = document.getElementById('authorizeDeviceModal');
    if (authModal) authModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) denyQrAuthorize(); });
    const qrScanModal = document.getElementById('qrScannerModal');
    if (qrScanModal) qrScanModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) stopQrScanner(); });
    const inviteModal = document.getElementById('inviteQrModal');
    if (inviteModal) inviteModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeInviteQr(); });

    // Backdrop click for feedback & ticket modals
    const feedbackModal = document.getElementById('feedbackModal');
    if (feedbackModal) feedbackModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeFeedbackModal(); });
    const ticketListModal = document.getElementById('ticketListModal');
    if (ticketListModal) ticketListModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeTicketList(); });
    const ticketDetailModal = document.getElementById('ticketDetailModal');
    if (ticketDetailModal) ticketDetailModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) closeTicketDetail(); });
    // duplicateTagModal uses promise-based cleanup — overlay click triggers Cancel
    const dupTagModal = document.getElementById('duplicateTagModal');
    if (dupTagModal) dupTagModal.addEventListener('click', (e) => { if (e.target === e.currentTarget) document.getElementById('duplicateTagCancel').click(); });
});

// BUG 2 FIX: Escape key closes any active modal overlay
document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const activeOverlay = document.querySelector('.modal-overlay.active');
    if (!activeOverlay) return;
    e.preventDefault();
    const id = activeOverlay.id;
    if (id === 'qrLoginModal') cancelQrLogin();
    else if (id === 'qrScannerModal') stopQrScanner();
    else if (id === 'authorizeDeviceModal') denyQrAuthorize();
    else if (id === 'inviteQrModal') closeInviteQr();
    else if (id === 'noteModal') closeModal();
    else if (id === 'feedbackModal') closeFeedbackModal();
    else if (id === 'pinModal') closePinModal();
    else if (id === 'profileMgmtModal') closeProfileMgmt();
    else if (id === 'fileSelectModal') closeFileSelectModal();
    else if (id === 'ticketListModal') closeTicketList();
    else if (id === 'ticketDetailModal') closeTicketDetail();
    else if (id === 'confirmModal') { document.getElementById('confirmModalCancel').click(); }
    else if (id === 'duplicateTagModal') { document.getElementById('duplicateTagCancel').click(); }
    else if (id === 'adminListModal') closeAdminModal();
    else activeOverlay.classList.remove('active'); // generic fallback
});

// Re-acquire wake lock on visibility change + log lifecycle events
document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible') {
        logger.info('LIFECYCLE', 'App returned to foreground', {
            online: navigator.onLine,
            isRecording: state.isRecording,
            isPaused: state.isPaused,
            hasPendingUpload: !!state.pendingUploadBlob,
            connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
        });
        // v2.11.0 FIX B: Re-acquire wake lock if recording OR paused
        if (state.isRecording || state.isPaused) await requestWakeLock();
    } else {
        logger.info('LIFECYCLE', 'App went to background', {
            wasRecording: state.isRecording,
            isPaused: state.isPaused,
            hasPendingUpload: !!state.pendingUploadBlob,
            recordingSegments: state.recordingSegments.length,
        });
    }
});

// Auto-retry upload when network comes back
window.addEventListener('online', () => {
    logger.info('NETWORK', 'Connection restored', {
        eventTimestamp: new Date().toISOString(),
        hasPendingUpload: !!state.pendingUploadBlob,
        effectiveType: navigator.connection?.effectiveType,
        downlink: navigator.connection?.downlink,
        rtt: navigator.connection?.rtt,
        wasRecording: state.isRecording,
        sessionUptimeMs: Date.now() - PAGE_LOAD_TIME,
    });
    if (state.pendingUploadBlob) {
        setStatus('recordStatus', 'Network restored! Retrying upload...', 'uploading');
        retryUpload();
    }
});


// ============================================================================
// AUTOMATIC ERROR REPORTING
// ============================================================================
let _lastDiagnosticSentAt = 0;

function sendDiagnosticReport(triggerEntry) {
    // Don't send if not logged in or offline
    const authToken = getSessionToken(); // FIX B12: single accessor
    if (!authToken || !navigator.onLine) return;
    // Throttle: max 1 report per 30 seconds
    const now = Date.now();
    if (now - _lastDiagnosticSentAt < 30000) return;
    _lastDiagnosticSentAt = now;

    const recentLogs = sanitizeLogs(logger.getLogs().slice(-50));
    const payload = {
        sessionId: SESSION_ID,
        appVersion: APP_VERSION,
        timestamp: new Date().toISOString(),
        deviceInfo: logger.getDeviceInfo(),
        // FIX N7 (E21): errorContext bypassed sanitizeLogs entirely, so the
        // triggering message and error text went to scribe/diagnostics/ in S3
        // exactly as written. Both are redacted now.
        // Revert note: to roll back, restore
        //   message: triggerEntry.message,
        //   error: triggerEntry.error || null,
        errorContext: {
            category: triggerEntry.category,
            message: redactKeyShapedValues(String(triggerEntry.message || '')),
            error: triggerEntry.error ? redactForLogs(triggerEntry.error, 500) : null,
        },
        recentLogs,
        appState: {
            isRecording: state.isRecording,
            segmentCount: state.recordingSegments.length,
            pendingUpload: !!state.pendingUploadBlob,
            pendingBlobSize: state.pendingUploadBlob ? state.pendingUploadBlob.size : 0,
        },
    };

    // Fire-and-forget — never block UI or show errors
    fetch(API_BASE + '/diagnostics/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + authToken },
        body: JSON.stringify(payload),
    }).catch(() => {}); // Silently ignore failures
}

// Auto-report on RECORDING or UPLOAD errors
logger.onNewEntry((entry) => {
    if (!entry) return;
    if (entry.level === 'ERROR' && (entry.category === 'RECORDING' || entry.category === 'UPLOAD')) {
        sendDiagnosticReport(entry);
    }
});


// ============================================================================
// DEBUG PANEL & FEEDBACK SYSTEM
// ============================================================================
// Initialize debug panel when DOM is ready
document.addEventListener('DOMContentLoaded', () => initDebugPanel(openFeedbackModal));
// Initialize Quick Note mode toggle buttons
document.addEventListener('DOMContentLoaded', () => initNoteModeToggle());

// Initialize the FEAT-002 "returning patient?" pickers on the Record and Upload pages
document.addEventListener('DOMContentLoaded', () => { initRecordPatientPicker(); initUploadPatientPicker(); });

// Log offline events
window.addEventListener('offline', () => {
    logger.warn('NETWORK', 'Connection lost', {
        eventTimestamp: new Date().toISOString(),
        wasRecording: state.isRecording,
        hasPendingUpload: !!state.pendingUploadBlob,
        isPaused: state.isPaused,
        recordingSegments: state.recordingSegments.length,
        sessionUptimeMs: Date.now() - PAGE_LOAD_TIME,
    });
});

// Log network connection type changes (critical for debugging mobile connection drops)
if (navigator.connection) {
    navigator.connection.addEventListener('change', () => {
        logger.info('NETWORK', 'Connection type changed', {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
            saveData: navigator.connection.saveData,
            wasRecording: state.isRecording,
            hasPendingUpload: !!state.pendingUploadBlob,
        });
    });
}

// ============================================================================
// PROVIDER PROFILES — Profile Picker
// ============================================================================

